
import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Table, TableBody, TableCell, TableHead, TableFooter, TableRow, Paper, Typography } from '@mui/material';

import { formatMoney } from '../../shared/formatters';
import { loadStart } from '../../redux/balancePerCategorySlice';
import { updateFilters } from '../../redux/transactionSlice';
import { downloadCSV } from '../../shared/utils';
import Filter from './filter';
import GroupComponent from './groupComponent';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  overflowX: 'auto',
  marginTop: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 98, 105, 0.3)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(0, 98, 105, 0.5)',
    },
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: 700,
  width: '100%',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 98, 105, 0.04)',
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: 'rgba(0, 98, 105, 0.05)',
  '& .MuiTableCell-head': {
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

const StickyFirstCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  position: 'sticky',
  left: 0,
  backgroundColor: 'white',
  zIndex: 2,
}));

const MoneyCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.938rem',
  fontWeight: 500,
  padding: '16px',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}));

function Report() {
  const dispatch = useDispatch();
  const result = useSelector(state => state.balancePerCategory.result);
  const shouldBeUpdated = useSelector(state => state.balancePerCategory.shouldBeUpdated);
  const lastUpdate = useSelector(state => state.balancePerCategory.lastUpdate);
  const categories = useSelector(state => state.category.items);
  
  const scrollContainerRef = useRef(null);
  const scrollContainerRef2 = useRef(null);
  const scrollContainerRef3 = useRef(null);
  
  const loadStartAction = useCallback(() => dispatch(loadStart()), [dispatch]);
  const updateTransactionFilters = (newFilters) => dispatch(updateFilters(newFilters));
  
  const navigate = useNavigate();

  useEffect(() => {
    if (shouldBeUpdated || new Date(lastUpdate).getTime() + 1000 * 10 < new Date().getTime()) {
      loadStartAction();
    }
  }, [shouldBeUpdated, lastUpdate, loadStartAction]);

  useEffect(() => {
    if (result.balance) {
      // Scroll to the end after a brief delay to ensure layout is complete
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
        }
        if (scrollContainerRef2.current) {
          scrollContainerRef2.current.scrollLeft = scrollContainerRef2.current.scrollWidth;
        }
        if (scrollContainerRef3.current) {
          scrollContainerRef3.current.scrollLeft = scrollContainerRef3.current.scrollWidth;
        }
      }, 100);
    }
  }, [result.balance]);

  const handleExport = () => {
    if (!result.balance) return;

    const months = Object.keys(result.balance);
    const creditKeys = Object.keys(result.groups.credit);
    const debitKeys = Object.keys(result.groups.debit);
    
    const exportData = [];

    // Add summary section
    exportData.push({
      category: 'RESUMO',
      ...months.reduce((acc, month) => ({ ...acc, [month]: '' }), {})
    });
    
    // Add credit summary
    const creditSummaryRow = { category: 'Crédito' };
    months.forEach(month => {
      creditSummaryRow[month] = formatMoney(result.creditBalance[month]);
    });
    exportData.push(creditSummaryRow);

    // Add debit summary
    const debitSummaryRow = { category: 'Débito' };
    months.forEach(month => {
      debitSummaryRow[month] = formatMoney(result.debitBalance[month]);
    });
    exportData.push(debitSummaryRow);

    // Add total balance summary
    const totalSummaryRow = { category: 'Saldo Total' };
    months.forEach(month => {
      totalSummaryRow[month] = formatMoney(result.balance[month]);
    });
    exportData.push(totalSummaryRow);

    // Add empty row
    exportData.push({
      category: '',
      ...months.reduce((acc, month) => ({ ...acc, [month]: '' }), {})
    });
    
    // Add credits section
    exportData.push({
      category: 'CRÉDITOS',
      ...months.reduce((acc, month) => ({ ...acc, [month]: '' }), {})
    });

    creditKeys.forEach(creditGroupKey => {
      const groupData = result.groups.credit[creditGroupKey];
      if (groupData && groupData.group) {
        const groupRow = { category: groupData.group.name };
        months.forEach(month => {
          groupRow[month] = formatMoney(groupData.balance[month] || 0);
        });
        exportData.push(groupRow);

        // Add nested categories
        const categoryKeys = Object.keys(groupData.categories || {});
        categoryKeys.forEach(categoryKey => {
          const categoryData = groupData.categories[categoryKey];
          if (categoryData && categoryData.category) {
            const categoryRow = { category: '  ' + categoryData.category.name };
            months.forEach(month => {
              categoryRow[month] = formatMoney(categoryData.balance[month] || 0);
            });
            exportData.push(categoryRow);
          }
        });
      }
    });

    // Add empty row
    exportData.push({
      category: '',
      ...months.reduce((acc, month) => ({ ...acc, [month]: '' }), {})
    });
    
    // Add debits section
    exportData.push({
      category: 'DÉBITOS',
      ...months.reduce((acc, month) => ({ ...acc, [month]: '' }), {})
    });

    debitKeys.forEach(debitGroupKey => {
      const groupData = result.groups.debit[debitGroupKey];
      if (groupData && groupData.group) {
        const groupRow = { category: groupData.group.name };
        months.forEach(month => {
          groupRow[month] = formatMoney(groupData.balance[month] || 0);
        });
        exportData.push(groupRow);

        // Add nested categories
        const categoryKeys = Object.keys(groupData.categories || {});
        categoryKeys.forEach(categoryKey => {
          const categoryData = groupData.categories[categoryKey];
          if (categoryData && categoryData.category) {
            const categoryRow = { category: '  ' + categoryData.category.name };
            months.forEach(month => {
              categoryRow[month] = formatMoney(categoryData.balance[month] || 0);
            });
            exportData.push(categoryRow);
          }
        });
      }
    });

    // Build columns map dynamically
    const columnsMap = [
      { field: 'category', title: 'Categoria' },
      ...months.map(month => ({ 
        field: month, 
        title: formatMonthHeader(month)
      }))
    ];

    downloadCSV(exportData, columnsMap, 'relatorio_saldo_por_categoria.csv');
  };

  if (!result.balance)
    return <div>No data</div>;

  const months = Object.keys(result.balance);
  const creditKeys = Object.keys(result.groups.credit);
  const debitKeys = Object.keys(result.groups.debit);

  return (
    <>
      <Typography variant="h4" component="h2" sx={{ 
        fontWeight: 700,
        color: '#1a1a1a',
        marginBottom: 3,
      }}>
        Saldo por categoria
      </Typography>
      <Filter onExport={handleExport}></Filter>
      <StyledPaper ref={scrollContainerRef}>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StickyFirstCell>Saldo</StickyFirstCell>
              {(months || []).map(month =>
                <StyledTableCell key={month} align="right">
                  {formatMonthHeader(month)}
                </StyledTableCell>
              )}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            <StyledTableRow hover>
              <StickyFirstCell component="th" scope="row">
                Crédito
              </StickyFirstCell>
              {(months || []).map(month =>
                <MoneyCell key={month} align="right">
                  {formatMoney(result.creditBalance[month])}
                </MoneyCell>
              )}
            </StyledTableRow>
            <StyledTableRow hover>
              <StickyFirstCell component="th" scope="row">
                Débito
              </StickyFirstCell>
              {(months || []).map(month =>
                <MoneyCell key={month} align="right">
                  {formatMoney(result.debitBalance[month])}
                </MoneyCell>
              )}
            </StyledTableRow>
          </TableBody>
          <TableFooter>
            <StyledTableRow hover>
              <StickyFirstCell component="th" scope="row" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                Saldo Total
              </StickyFirstCell>
              {(months || []).map(month =>
                <MoneyCell key={month} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  {formatMoney(result.balance[month])}
                </MoneyCell>
              )}
            </StyledTableRow>
          </TableFooter>
        </StyledTable>
      </StyledPaper>
      <br />
      <StyledPaper ref={scrollContainerRef2}>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StickyFirstCell>Créditos</StickyFirstCell>
              {(months || []).map(month =>
                <StyledTableCell key={month} align="right">
                  {formatMonthHeader(month)}
                </StyledTableCell>
              )}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {creditKeys.map(creditGroupKey =>
            <GroupComponent
                key={creditGroupKey}
                groupData={result.groups.credit[creditGroupKey]}
                months={months}
                updateTransactionFilters={updateTransactionFilters}
                navigate={navigate}
                categoryData={categories}
              />
            )}
          </TableBody>
          <TableFooter>
            <StyledTableRow hover>
              <StickyFirstCell component="th" scope="row" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                Total Créditos
              </StickyFirstCell>
              {(months || []).map(month =>
                <MoneyCell key={month} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  {formatMoney(result.creditBalance[month])}
                </MoneyCell>
              )}
            </StyledTableRow>
          </TableFooter>
        </StyledTable>
      </StyledPaper>
      <br />
      <StyledPaper ref={scrollContainerRef3}>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              <StickyFirstCell>Débitos</StickyFirstCell>
              {(months || []).map(month =>
                <StyledTableCell key={month} align="right">
                  {formatMonthHeader(month)}
                </StyledTableCell>
              )}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {debitKeys.map(debitGroupKey =>
              <GroupComponent
                key={debitGroupKey}
                groupData={result.groups.debit[debitGroupKey]}
                months={months}
                updateTransactionFilters={updateTransactionFilters}
                navigate={navigate}
                categoryData={categories}
              />
            )}
          </TableBody>
          <TableFooter>
            <StyledTableRow hover>
              <StickyFirstCell component="th" scope="row" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                Total Débitos
              </StickyFirstCell>
              {(months || []).map(month =>
                <MoneyCell key={month} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                  {formatMoney(result.debitBalance[month])}
                </MoneyCell>
              )}
            </StyledTableRow>
          </TableFooter>
        </StyledTable>
      </StyledPaper>
    </>
  );
}

export default Report;

function formatMonthHeader(month) {
  return month === 'average' ? 'Média' : month === 'total' ? 'Total' : month;
}
